/**
 * AppContext - Application Context for Navigation State
 *
 * Purpose: Track which application the user is currently in, enabling
 * context-aware navigation and header behavior.
 *
 * Design Reference: Phase 60 design.md - "AppContext State Management (D-UX-1)"
 */

import { createContext, useContext, useMemo, ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Application identifiers matching sidebar navigation structure
 * - cantera: Talent Pipeline (finding and developing talent)
 * - compas: RAG/Knowledge (guiding users through information)
 * - admin: Administration pages
 * - shared: Dashboard, Profile, and other shared pages
 */
export type AppName = 'cantera' | 'compas' | 'admin' | 'shared'

export interface AppContextValue {
  /** Current application identifier */
  currentApp: AppName
  /** Display name for the current app (shown in header) */
  appDisplayName: string
  /** Whether the header should show quick-switch tabs */
  showHeaderTabs: boolean
  /** Whether current app is Cantera (Talent Pipeline) */
  isCantera: boolean
  /** Whether current app is Compas (RAG/Knowledge) */
  isCompas: boolean
  /** Whether current app is Admin */
  isAdmin: boolean
}

const AppContext = createContext<AppContextValue | null>(null)

interface AppProviderProps {
  children: ReactNode
}

/**
 * AppProvider - Provides application context based on current route
 *
 * Must be placed inside BrowserRouter (uses useLocation)
 */
export function AppProvider({ children }: AppProviderProps) {
  const location = useLocation()

  const value = useMemo<AppContextValue>(() => {
    const path = location.pathname

    // Cantera (Talent Pipeline) - /talent-pipeline/*
    if (path.startsWith('/talent-pipeline')) {
      return {
        currentApp: 'cantera',
        appDisplayName: 'Cantera',
        showHeaderTabs: true, // Cantera has quick-switch tabs
        isCantera: true,
        isCompas: false,
        isAdmin: false,
      }
    }

    // Compas (RAG/Knowledge) - /chat/*, /knowledge
    if (path.startsWith('/chat') || path.startsWith('/knowledge')) {
      return {
        currentApp: 'compas',
        appDisplayName: 'Compás',
        showHeaderTabs: false, // Compas has no tabs currently
        isCantera: false,
        isCompas: true,
        isAdmin: false,
      }
    }

    // Admin - /admin/*
    if (path.startsWith('/admin')) {
      return {
        currentApp: 'admin',
        appDisplayName: 'Administración',
        showHeaderTabs: false,
        isCantera: false,
        isCompas: false,
        isAdmin: true,
      }
    }

    // Shared (Dashboard, Profile, etc.)
    return {
      currentApp: 'shared',
      appDisplayName: 'NEXO',
      showHeaderTabs: false,
      isCantera: false,
      isCompas: false,
      isAdmin: false,
    }
  }, [location.pathname])

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

/**
 * Hook to access application context
 *
 * @throws Error if used outside of AppProvider
 */
export function useAppContext(): AppContextValue {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider')
  }
  return context
}

// Alias for consistency with AuthContext naming
export const useApp = useAppContext

AppProvider.displayName = 'AppProvider'
